'use client';

import React, { useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Bell, CheckCheck } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
	useNotifications,
	type Notification,
} from '@/contexts/NotificationContext';

export default function NotificationBell() {
	const router = useRouter();
	const {
		notifications,
		unreadCount,
		loading,
		markAsRead,
		markAllRead,
		open,
		setOpen,
	} = useNotifications();
	const menuRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (
				menuRef.current &&
				!menuRef.current.contains(e.target as Node)
			) {
				setOpen(false);
			}
		}
		function handleEscape(e: KeyboardEvent) {
			if (e.key === 'Escape') setOpen(false);
		}
		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleEscape);
		return () => {
			document.removeEventListener(
				'mousedown',
				handleClickOutside,
			);
			document.removeEventListener(
				'keydown',
				handleEscape,
			);
		};
	}, [setOpen]);

	const handleOpen = () => setOpen(!open);

	const handleItemClick = async (n: Notification) => {
		setOpen(false);
		if (!n.read) await markAsRead(n._id);
		if (n.link) router.push(n.link);
	};

	const handleMarkAll = async () => {
		await markAllRead();
	};

	return (
		<div className="relative" ref={menuRef}>
			<button
				aria-label="Notifications"
				onClick={handleOpen}
				className="relative inline-flex items-center justify-center p-2 rounded-md hover:bg-gray-100 focus:outline-none"
			>
				<Bell className="w-6 h-6 text-gray-700" />
				{unreadCount > 0 && (
					<span
						className="absolute top-0 right-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold"
						style={{
							background: '#FFCF23',
							color: '#051449',
						}}
					>
						{unreadCount > 99 ? '99+' : unreadCount}
					</span>
				)}
			</button>

			{open && (
				<div className="absolute right-0 mt-2 w-80 sm:w-96 max-w-[85vw] bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top-right z-50">
					<div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
						<p className="text-sm font-semibold text-gray-900">
							Notifications
						</p>
						{unreadCount > 0 && (
							<button
								type="button"
								onClick={handleMarkAll}
								className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-[#051449]"
							>
								<CheckCheck className="w-3.5 h-3.5" />
								Mark all read
							</button>
						)}
					</div>

					<div className="max-h-96 overflow-y-auto">
						{loading && notifications.length === 0 ? (
							<p className="px-4 py-8 text-center text-sm text-gray-400">
								Loading notifications...
							</p>
						) : notifications.length === 0 ? (
							<p className="px-4 py-8 text-center text-sm text-gray-400">
								No notifications yet
							</p>
						) : (
							notifications.map((n) => (
								<button
									key={n._id}
									type="button"
									onClick={() => handleItemClick(n)}
									className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
										!n.read
											? 'bg-[#F8FAFF]'
											: 'bg-white'
									}`}
								>
									<div className="flex items-start gap-2">
										{!n.read && (
											<span className="mt-1.5 w-2 h-2 rounded-full shrink-0 bg-[#FFCF23]" />
										)}
										<div className="flex-1 min-w-0">
											<p className="text-sm font-medium text-gray-900 truncate">
												{n.title}
											</p>
											{n.body && (
												<p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
													{n.body}
												</p>
											)}
											<p className="text-[10px] text-gray-400 mt-1">
												{formatDistanceToNow(
													new Date(
														n.createdAt,
													),
													{ addSuffix: true },
												)}
											</p>
										</div>
									</div>
								</button>
							))
						)}
					</div>
				</div>
			)}
		</div>
	);
}
